// ============================================================================
// cabinet/CabinetShell.tsx — ОБОЛОЧКА ПРИЛОЖЕНИЯ «КАБИНЕТ».
//
// Задачи оболочки (SRP):
//   1) шапка кабинета (название + пользователь);
//   2) полоса вкладок из РЕЕСТРА (lib/cabinet/tabs.ts) — оболочка не знает,
//      какие вкладки существуют, и не содержит условий вида «если support»;
//   3) область содержимого активной вкладки;
//   4) мобильный вид: та же полоса вкладок внизу (над общим доком оболочки).
//
// Вкладки регистрируются при старте клиента (registerCabinetTab). Новая
// вкладка поставки/модуля появляется здесь без правок этого файла.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { LayoutDashboard } from 'lucide-react';
import type { TabItem } from '@/components/ui/TabBar';
import { TabBar } from '@/components/ui/TabBar';
import { useAuthStore } from '@/store/authStore';
import { createLogger } from '@/lib/logger';
import { CABINET_TITLE, type CabinetTab } from '@/lib/cabinet/tabs';

const log = createLogger('CabinetShell');

/** Последняя открытая вкладка (личная настройка просмотра, не состав кабинета). */
const LAST_TAB_KEY = 'hydra_cabinet_tab';

function readLastTab(): string {
  try {
    return localStorage.getItem(LAST_TAB_KEY) || '';
  } catch (e) {
    log.warn('Не удалось прочитать последнюю вкладку кабинета', { error: String(e) });
    return '';
  }
}

function saveLastTab(code: string): void {
  try {
    localStorage.setItem(LAST_TAB_KEY, code);
  } catch (e) {
    log.warn('Не удалось сохранить последнюю вкладку кабинета', { error: String(e) });
  }
}

export interface CabinetShellProps {
  /** Вкладки из реестра (порядок уже определён listCabinetTabs). */
  tabs: CabinetTab[];
  /** Название кабинета (по умолчанию «Кабинет»; позже — настройка базы/роли). */
  title?: string;
  /** Начальная вкладка (например, из глубокой ссылки), если она зарегистрирована. */
  initialCode?: string;
}

function tabLabel(tab: CabinetTab) {
  const Icon = tab.icon;
  return (
    <span className="flex items-center gap-1.5" title={tab.description || tab.title}>
      <Icon className="h-3.5 w-3.5" />
      <span>{tab.title}</span>
    </span>
  );
}

export function CabinetShell({ tabs, title = CABINET_TITLE, initialCode = '' }: CabinetShellProps) {
  const userName = useAuthStore((s) => s.user?.name || '');
  const firstCode = tabs[0]?.code || '';

  const [activeCode, setActiveCode] = useState(() => {
    const wanted = initialCode || readLastTab();
    return tabs.some((t) => t.code === wanted) ? wanted : firstCode;
  });

  // Реестр мог измениться (вкладку сняли/добавили) — активная всегда существует.
  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some((t) => t.code === activeCode)) setActiveCode(tabs[0].code);
  }, [tabs, activeCode]);

  // Глубокая ссылка на конкретную вкладку (если она зарегистрирована).
  useEffect(() => {
    if (!initialCode) return;
    if (tabs.some((t) => t.code === initialCode)) setActiveCode(initialCode);
  }, [initialCode, tabs]);

  const selectTab = (code: string) => {
    setActiveCode(code);
    saveLastTab(code);
  };

  const items = useMemo<TabItem[]>(
    () => tabs.map((t) => ({ id: t.code, label: tabLabel(t) })),
    [tabs],
  );

  const activeTab = tabs.find((t) => t.code === activeCode) || tabs[0] || null;
  const ActiveComponent = activeTab?.Component;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Шапка кабинета: название и текущий пользователь */}
      <div className="flex shrink-0 items-center gap-2.5 border-b bg-card/40 px-4 py-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <LayoutDashboard className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">{title}</div>
          {userName && <div className="truncate text-[11px] text-muted-foreground">{userName}</div>}
        </div>
      </div>

      {/* Вкладки — на десктопе под шапкой */}
      {items.length > 0 && (
        <div className="hidden shrink-0 items-center gap-2 border-b bg-background/60 px-3 py-1.5 md:flex">
          <TabBar tabs={items} active={activeCode} onChange={selectTab} group="cabinet-desktop" />
        </div>
      )}

      {/* Содержимое активной вкладки (каждая вкладка владеет своей разметкой) */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {ActiveComponent ? (
          <ActiveComponent />
        ) : (
          <div className="flex h-full items-center justify-center p-6">
            <div className="max-w-md rounded-lg border bg-card p-5 text-center">
              <LayoutDashboard className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <div className="text-sm font-medium">Вкладки кабинета не зарегистрированы</div>
              <div className="mt-1 text-xs text-muted-foreground">
                Ни одна вкладка не добавлена в реестр кабинета. Вкладки регистрируются
                приложениями и поставками — обратитесь к администратору базы.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Вкладки — на мобильном внизу (выше общего дока оболочки) */}
      {items.length > 0 && (
        <div className="shrink-0 border-t bg-card/70 px-2 py-1.5 md:hidden">
          <TabBar
            tabs={items}
            active={activeCode}
            onChange={selectTab}
            group="cabinet-mobile"
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}
